"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  RefreshCcw,
  Printer,
  Edit2,
  X,
  PlusCircle,
  ImageIcon,
  Phone,
  Mail,
  Hash,
  DollarSign,
  CreditCard,
  Smartphone,
  ChevronRight,
  Check,
  Trash2,
  Calendar,
  Clock,
  Users,
  CirclePlus,
  MessageCircle,
  Tag,
} from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import AccountSwitch from "../../components/AccountSwitch"
import WhatsAppSender from "./WhatsAppSender"
import { API_URL } from "../../api/config"

const MODAL_TYPES = {
  EDIT: "edit",
  MENU: "menu",
  VARIANT: "variant",
  PAYMENT: "payment",
  WHATSAPP: "whatsapp",
  DETAILS: "details",
}

const Dashboard = () => {
  const navigate = useNavigate()
  const { userToken } = useAuth()

  // Main state
  const [loading, setLoading] = useState(true)
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [restaurantInfo, setRestaurantInfo] = useState(null)
  const [bills, setBills] = useState([])
  const [todayRevenue, setTodayRevenue] = useState(0)
  const [products, setProducts] = useState([])

  const [selectedItems, setSelectedItems] = useState([])
  const [selectedBill, setSelectedBill] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedVariants, setSelectedVariants] = useState([])

  const [activeModal, setActiveModal] = useState(null)

  const [showAccountSwitch, setShowAccountSwitch] = useState(false)

  const [discountPercentage, setDiscountPercentage] = useState(0)

  const fetchRestaurantInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          Accept: "application/json",
        },
      })

      if (!response.ok) throw new Error("Failed to fetch restaurant info")

      const data = await response.json()
      setRestaurantInfo({
        name: data.restaurantName,
        logoUrl: data.logoUrl,
        address: data.location,
        phoneNumber: data.phoneNumber,
        email: data.businessEmail,
        gstin: data.gstin,
      })
    } catch (error) {
      console.error("Failed to fetch restaurant info:", error)
      alert("Error: Failed to load restaurant information")
    }
  }

  const isBillEditable = (bill) => {
    if (!bill.createdAt) return false

    const billDate = new Date(bill.createdAt)
    const currentDate = new Date()
    const timeDifferenceInMinutes = (currentDate - billDate) / (1000 * 60)

    return timeDifferenceInMinutes <= 15
  }

  // Check if bill has a valid mobile number for WhatsApp
  const hasValidMobileNumber = (bill) => {
    return (
      bill.mobileNumber && bill.mobileNumber.trim() !== "" && /^[6-9]\d{9}$/.test(bill.mobileNumber.replace(/\D/g, ""))
    )
  }

  const fetchTodaysBills = async () => {
    if (!userToken) return

    try {
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const queryParams = new URLSearchParams({
        startDate: today.toISOString(),
        endDate: new Date().toISOString(),
        limit: 1000,
      }).toString()

      const response = await fetch(`${API_URL}/bills?${queryParams}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          Accept: "application/json",
        },
      })

      if (!response.ok) throw new Error("Failed to fetch bills")

      const data = await response.json()
      setBills(data.bills || [])

      const revenue = (data.bills || []).reduce(
        (total, bill) => total + (bill.status === "COMPLETED" ? bill.totalAmount : 0),
        0,
      )
      setTodayRevenue(revenue)
    } catch (error) {
      console.error("Failed to fetch bills:", error)
      alert("Error: Failed to fetch bills")
    } finally {
      setLoading(false)
    }
  }

  const fetchProducts = async () => {
    setLoadingProducts(true)
    try {
      const response = await fetch(`${API_URL}/products`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          Accept: "application/json",
        },
      })

      if (!response.ok) throw new Error("Failed to fetch products")
      const data = await response.json()
      setProducts(data)
    } catch (error) {
      console.error("Failed to fetch products:", error)
      alert("Error: Failed to load menu items")
    } finally {
      setLoadingProducts(false)
    }
  }

  const handleItemSelect = (product) => {
    setSelectedProduct(product)
    if (product.variants?.length > 0) {
      setSelectedVariants([])
      setActiveModal(MODAL_TYPES.VARIANT)
    } else {
      const price = product.pricingType === "basePrice" ? product.basePrice : product.sellingPrice
      addItemToSelection({
        _id: product._id,
        itemName: product.itemName,
        price,
        quantity: 1,
      })
    }
  }

  const handleVariantSelection = (variant) => {
    const newVariant = {
      _id: selectedProduct._id,
      itemName: `${selectedProduct.itemName} - ${variant.name}`,
      price: variant.price,
      variant,
      quantity: 1,
    }

    setSelectedVariants((prev) => {
      const existingIndex = prev.findIndex((v) => v._id === newVariant._id && v.variant.name === variant.name)

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated.splice(existingIndex, 1)
        return updated
      }
      return [...prev, newVariant]
    })
  }

  const handleVariantsConfirm = () => {
    selectedVariants.forEach(addItemToSelection)
  }

  const addItemToSelection = (item) => {
    setSelectedItems((prev) => {
      const existingItem = prev.find((i) => i._id === item._id && i.variant?.name === item.variant?.name)

      if (existingItem) {
        return prev.map((i) =>
          i._id === item._id && i.variant?.name === item.variant?.name ? { ...i, quantity: i.quantity + 1 } : i,
        )
      }
      return [...prev, item]
    })
  }

  const handleDeleteItem = (itemId) => {
    if (window.confirm("Are you sure you want to remove this item?")) {
      setSelectedItems((prev) => prev.filter((item) => item._id !== itemId))
    }
  }

  const updatePaymentMethod = async (billId, newPaymentMethod) => {
    const bill = bills.find((b) => b._id === billId)

    if (!bill || !isBillEditable(bill)) {
      alert("Error: This bill can no longer be edited")
      return
    }

    try {
      const response = await fetch(`${API_URL}/bills/${billId}/payment-method`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ paymentMethod: newPaymentMethod }),
      })

      if (!response.ok) throw new Error("Failed to update payment method")

      await fetchTodaysBills()
      alert("Success: Payment method updated successfully")
    } catch (error) {
      console.error("Failed to update payment method:", error)
      alert("Error: Failed to update payment method")
    }
  }

  const handleEditBill = (bill) => {
    setSelectedBill(bill)
    setSelectedItems(bill.items || [])
    setActiveModal(MODAL_TYPES.EDIT)
  }

  const handleBillDetails = (bill) => {
    setSelectedBill(bill)
    setDiscountPercentage(bill.discountPercentage || 0)
    setActiveModal(MODAL_TYPES.DETAILS)
  }

  const handlePrint = async (billId) => {
    try {
      navigate(`/print-bill/${billId}`, {
        state: {
          restaurantInfo: restaurantInfo,
        },
      })
    } catch (error) {
      console.error("Failed to navigate to print screen:", error)
      alert("Error: Failed to open print screen")
    }
  }

  // Handle WhatsApp send
  const handleWhatsAppSend = (bill) => {
    setSelectedBill(bill)
    setActiveModal(MODAL_TYPES.WHATSAPP)
  }

  const handleSaveBillChanges = async () => {
    if (!selectedBill) return

    try {
      const response = await fetch(`${API_URL}/bills/${selectedBill._id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: selectedItems.map((item) => ({
            productId: item._id,
            itemName: item.itemName,
            quantity: item.quantity,
            price: item.price,
            variant: item.variant,
          })),
        }),
      })

      if (!response.ok) throw new Error("Failed to update bill")

      await fetchTodaysBills()
      setActiveModal(null)
      setSelectedItems([])
      alert("Success: Bill updated successfully")
    } catch (error) {
      console.error("Failed to update bill:", error)
      alert("Error: Failed to update bill")
    }
  }

  // Update the handleSaveBillDetails function to calculate and send the new total amount
  const handleSaveBillDetails = async () => {
    if (!selectedBill) return

    try {
      // Calculate the new total amount after discount
      const subtotal = selectedBill.subtotal || 0
      const cgst = selectedBill.cgst || 0
      const sgst = selectedBill.sgst || 0
      const totalBeforeDiscount = subtotal + cgst + sgst
      const discountAmount = (totalBeforeDiscount * discountPercentage) / 100
      const newTotalAmount = totalBeforeDiscount - discountAmount

      const response = await fetch(`${API_URL}/bills/${selectedBill._id}`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discountPercentage: Number(discountPercentage),
          discountAmount: discountAmount,
          totalAmount: newTotalAmount,
        }),
      })

      if (!response.ok) throw new Error("Failed to update bill details")

      await fetchTodaysBills()
      setActiveModal(null)
      alert("Success: Bill details updated successfully")
    } catch (error) {
      console.error("Failed to update bill details:", error)
      alert("Error: Failed to update bill details")
    }
  }

  // Navigate to new bill page
  const handleNewSale = () => {
    navigate("/new-bill")
  }

  // Format date to display in a readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date)
  }

  // Calculate stats
  const completedBills = bills.filter((bill) => bill.status === "COMPLETED").length
  const averageTransaction = bills.length > 0 ? todayRevenue / completedBills : 0

  useEffect(() => {
    fetchRestaurantInfo()
    fetchTodaysBills()
  }, [])

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    )
  }

  // Render methods for modals
  const renderPaymentMethodModal = () =>
    activeModal === MODAL_TYPES.PAYMENT && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-lg rounded-t-lg overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="text-lg font-medium">Update Payment Method</h3>
            <button onClick={() => setActiveModal(null)} className="text-white hover:text-gray-200 focus:outline-none">
              <X size={20} />
            </button>
          </div>

          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {["CASH", "CARD", "UPI"].map((method) => (
              <button
                key={method}
                className="flex justify-between items-center w-full px-6 py-4 text-left hover:bg-blue-50 border-b border-gray-100 rounded-md mb-2 transition-all"
                onClick={() => {
                  updatePaymentMethod(selectedBill._id, method)
                  setActiveModal(null)
                }}
              >
                <div className="flex items-center">
                  {method === "CASH" ? (
                    <div className="p-3 bg-green-100 rounded-full mr-4">
                      <DollarSign size={24} className="text-green-600" />
                    </div>
                  ) : method === "CARD" ? (
                    <div className="p-3 bg-blue-100 rounded-full mr-4">
                      <CreditCard size={24} className="text-blue-600" />
                    </div>
                  ) : (
                    <div className="p-3 bg-purple-100 rounded-full mr-4">
                      <Smartphone size={24} className="text-purple-600" />
                    </div>
                  )}
                  <div>
                    <span className="text-gray-800 font-medium text-lg">{method}</span>
                    <p className="text-gray-500 text-sm">Tap to set as payment method</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            ))}
          </div>
        </div>
      </div>
    )

  const renderVariantModal = () =>
    activeModal === MODAL_TYPES.VARIANT && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-lg rounded-t-lg overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="text-lg font-medium">Select Variants</h3>
            <button
              onClick={() => setActiveModal(activeModal === MODAL_TYPES.MENU ? MODAL_TYPES.MENU : MODAL_TYPES.EDIT)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <h4 className="text-xl font-medium text-gray-700 mb-4">{selectedProduct?.itemName}</h4>

            <div className="space-y-3 mb-6">
              {selectedProduct?.variants.map((variant) => {
                const isSelected = selectedVariants.some(
                  (v) => v._id === selectedProduct._id && v.variant.name === variant.name,
                )

                return (
                  <button
                    key={variant.name}
                    className={`flex justify-between items-center w-full px-5 py-4 rounded-lg text-left transition-all ${
                      isSelected ? "bg-blue-500 text-white shadow-md" : "bg-gray-50 text-gray-800 hover:bg-gray-100"
                    }`}
                    onClick={() => handleVariantSelection(variant)}
                  >
                    <div>
                      <span className="font-medium text-lg">{variant.name}</span>
                      <p className={`text-sm mt-1 ${isSelected ? "text-blue-100" : "text-gray-500"}`}>
                        ₹{variant.price.toFixed(2)}
                      </p>
                    </div>
                    {isSelected && <Check size={20} />}
                  </button>
                )
              })}
            </div>

            <button
              className={`w-full py-4 px-6 rounded-lg font-medium text-white shadow-md transition-all ${
                selectedVariants.length > 0
                  ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              onClick={() => {
                handleVariantsConfirm()
                setActiveModal(activeModal === MODAL_TYPES.MENU ? MODAL_TYPES.MENU : MODAL_TYPES.EDIT)
              }}
              disabled={selectedVariants.length === 0}
            >
              Add Selected Variants
            </button>
          </div>
        </div>
      </div>
    )

  const renderEditBillModal = () =>
    activeModal === MODAL_TYPES.EDIT && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-lg sm:rounded-lg rounded-t-lg overflow-hidden shadow-xl transform transition-all sm:max-h-[90vh]">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="text-lg font-medium">Edit Bill #{selectedBill?.billNumber}</h3>
            <button onClick={() => setActiveModal(null)} className="text-white hover:text-gray-200 focus:outline-none">
              <X size={20} />
            </button>
          </div>

          <div className="px-6 py-4 max-h-[calc(90vh-10rem)] overflow-y-auto">
            {selectedItems.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
                <p className="text-lg">No items added to this bill.</p>
                <p className="text-sm text-gray-400 mt-1">Add items using the button below.</p>
              </div>
            ) : (
              <div className="space-y-3 mb-6">
                {selectedItems.map((item, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-100 transition-all"
                  >
                    <div>
                      <h4 className="font-medium text-gray-800">{item.itemName}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        <span className="font-semibold">{item.quantity}</span> x ₹{item.price.toFixed(2)} = ₹
                        {(item.quantity * item.price).toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteItem(item._id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-full transition-all"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col space-y-3">
              <button
                className="w-full py-4 px-6 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 flex justify-center items-center transition-all"
                onClick={() => {
                  setActiveModal(MODAL_TYPES.MENU)
                  fetchProducts()
                }}
              >
                <PlusCircle size={22} className="mr-2" />
                Add Items
              </button>

              <button
                className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg font-medium shadow-md transition-all"
                onClick={handleSaveBillChanges}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    )

  const renderMenuModal = () =>
    activeModal === MODAL_TYPES.MENU && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-lg sm:rounded-lg rounded-t-lg overflow-hidden shadow-xl transform transition-all sm:max-h-[90vh]">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h3 className="text-lg font-medium">Select Menu Items</h3>
            <button
              onClick={() => setActiveModal(MODAL_TYPES.EDIT)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-10rem)]">
            {loadingProducts ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-3 border-b-3 border-blue-600"></div>
              </div>
            ) : (
              <div className="p-2">
                {products.map((item) => (
                  <button
                    key={item._id}
                    className="w-full text-left px-6 py-5 border-b border-gray-100 hover:bg-gray-50 transition-all"
                    onClick={() => handleItemSelect(item)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-gray-800 text-lg">{item.itemName}</span>
                        {item.description && <p className="text-gray-500 text-sm mt-1">{item.description}</p>}
                      </div>
                      <div className="text-right">
                        {item.variants && item.variants.length > 0 ? (
                          <div className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                            {item.variants.length} variants
                          </div>
                        ) : (
                          <div className="font-semibold text-gray-700">
                            ₹{(item.pricingType === "basePrice" ? item.basePrice : item.sellingPrice).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200">
            <button
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg font-medium shadow-md transition-all"
              onClick={() => setActiveModal(MODAL_TYPES.EDIT)}
            >
              Done Adding Items
            </button>
          </div>
        </div>
      </div>
    )

  // Update the renderDetailsModal function to show the calculated discount and new total
  const renderDetailsModal = () =>
    activeModal === MODAL_TYPES.DETAILS && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-lg rounded-t-lg overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
            <h3 className="text-lg font-medium">Bill Details #{selectedBill?.billNumber}</h3>
            <button onClick={() => setActiveModal(null)} className="text-white hover:text-gray-200 focus:outline-none">
              <X size={20} />
            </button>
          </div>

          <div className="px-6 py-4">
            <div className="mb-4">
              <label htmlFor="discount" className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-1">
                  <Tag size={16} />
                  <span>Discount Percentage (%):</span>
                </div>
              </label>
              <input
                id="discount"
                type="number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., 10"
                value={discountPercentage}
                onChange={(e) => {
                  const num = Number(e.target.value)
                  if (!isNaN(num) && num >= 0 && num <= 100) {
                    setDiscountPercentage(num)
                  }
                }}
                min="0"
                max="100"
              />
            </div>

            {selectedBill && (
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span>₹{selectedBill.subtotal?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">CGST:</span>
                    <span>₹{selectedBill.cgst?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">SGST:</span>
                    <span>₹{selectedBill.sgst?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total before discount:</span>
                    <span>
                      ₹{((selectedBill.subtotal || 0) + (selectedBill.cgst || 0) + (selectedBill.sgst || 0)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-orange-600 font-medium">
                    <span>Discount ({discountPercentage}%):</span>
                    <span>
                      -₹
                      {(
                        (((selectedBill.subtotal || 0) + (selectedBill.cgst || 0) + (selectedBill.sgst || 0)) *
                          discountPercentage) /
                        100
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-blue-600 font-bold pt-2 border-t border-gray-200">
                    <span>New Total:</span>
                    <span>
                      ₹
                      {(
                        ((selectedBill.subtotal || 0) + (selectedBill.cgst || 0) + (selectedBill.sgst || 0)) *
                        (1 - discountPercentage / 100)
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg mr-2 hover:bg-gray-300 transition-all"
                onClick={() => setActiveModal(null)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:from-purple-600 hover:to-indigo-700 transition-all"
                onClick={handleSaveBillDetails}
              >
                Save Details
              </button>
            </div>
          </div>
        </div>
      </div>
    )

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Restaurant Info Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8 border border-gray-100">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-20 relative">
            {restaurantInfo?.logoUrl && (
              <div className="absolute -bottom-10 left-8 w-24 h-24 rounded-2xl bg-white p-2 shadow-lg">
                <img
                  src={restaurantInfo.logoUrl || "/placeholder.svg"}
                  alt="Restaurant Logo"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
            )}
          </div>

          <div className="p-8 pt-12 flex flex-col sm:flex-row sm:items-center">
            <div className="sm:ml-24">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{restaurantInfo?.name || "My Restaurant"}</h1>
              <div className="flex flex-wrap items-center text-gray-500 gap-6 mt-3">
                {restaurantInfo?.address && (
                  <div className="flex items-center">
                    <Mail size={16} className="mr-2 text-blue-500" />
                    <span>{restaurantInfo.address}</span>
                  </div>
                )}
                {restaurantInfo?.phoneNumber && (
                  <div className="flex items-center">
                    <Phone size={16} className="mr-2 text-blue-500" />
                    <span>{restaurantInfo.phoneNumber}</span>
                  </div>
                )}
                {restaurantInfo?.email && (
                  <div className="flex items-center">
                    <Mail size={16} className="mr-2 text-blue-500" />
                    <span>{restaurantInfo.email}</span>
                  </div>
                )}
                {restaurantInfo?.gstin && (
                  <div className="flex items-center">
                    <Hash size={16} className="mr-2 text-blue-500" />
                    <span>GSTIN: {restaurantInfo.gstin}</span>
                  </div>
                )}
              </div>
              {/* Button to show the AccountSwitch modal */}
              <button
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={() => setShowAccountSwitch(true)}
              >
                Switch Account
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-700 rounded-2xl shadow-lg overflow-hidden p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-blue-100 font-medium">Today's Revenue</h2>
                <div className="mt-2 text-3xl font-bold">₹{todayRevenue.toFixed(2)}</div>
              </div>
              <div className="p-3 bg-white bg-opacity-20 rounded-full">
                <DollarSign size={24} className="text-white" />
              </div>
            </div>
            <div className="mt-4 text-blue-100">{completedBills} completed transactions</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-6 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-gray-500 font-medium">Avg. Transaction</h2>
                <div className="mt-2 text-3xl font-bold text-gray-800">₹{averageTransaction.toFixed(2)}</div>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CreditCard size={24} className="text-blue-500" />
              </div>
            </div>
            <div className="mt-4 text-gray-500">Per completed transaction</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-6 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-gray-500 font-medium">Completed Bills</h2>
                <div className="mt-2 text-3xl font-bold text-gray-800">{completedBills}</div>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Check size={24} className="text-green-500" />
              </div>
            </div>
            <div className="mt-4 text-gray-500">Out of {bills.length} total bills</div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-6 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-gray-500 font-medium">Date</h2>
                <div className="mt-2 xl:text-2xl font-bold text-gray-600">{new Date().toLocaleDateString()}</div>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock size={24} className="text-yellow-500" />
              </div>
            </div>
            <div className="mt-4 text-gray-500">
              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>

        {/* New Sales Button - Fixed at bottom center */}
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <button
            onClick={handleNewSale}
            className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white text-lg font-medium py-4 px-8 rounded-full shadow-lg transition-all"
          >
            <CirclePlus size={24} className="mr-2" />
            New Sales
          </button>
        </div>

        {/* Bills Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-20 border border-gray-100">
          <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">Today's Bills</h2>
              <p className="text-gray-500 mt-1">Manage and view all transactions</p>
            </div>
            <button
              className="flex items-center justify-center bg-blue-50 text-blue-600 rounded-full w-10 h-10 hover:bg-blue-100 transition-all"
              onClick={fetchTodaysBills}
            >
              <RefreshCcw size={18} />
            </button>
          </div>

          {bills.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Calendar size={32} className="text-gray-400" />
              </div>
              <p className="text-lg font-medium text-gray-600">No bills found for today</p>
              <p className="text-gray-500 mt-2">Create a new bill to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {bills.map((bill) => (
                <div key={bill._id} className="p-6 hover:bg-gray-50 transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                    <div>
                      <div className="flex items-center">
                        <span className="text-lg font-semibold text-gray-800">Bill #{bill.billNumber}</span>
                        <span
                          className={`ml-3 px-3 py-1 text-xs rounded-full font-medium ${
                            bill.status === "COMPLETED"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {bill.status}
                        </span>
                        {/* WhatsApp indicator */}
                        {hasValidMobileNumber(bill) && (
                          <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full font-medium flex items-center">
                            <MessageCircle size={12} className="mr-1" />
                            WhatsApp
                          </span>
                        )}
                      </div>

                      <div className="mt-2 text-gray-500 flex items-center gap-4">
                        <div className="flex items-center">
                          <Clock size={16} className="mr-1" />
                          <span>{formatDate(bill.createdAt)}</span>
                        </div>

                        <div className="flex items-center">
                          <Users size={16} className="mr-1" />
                          <span>{bill.customerName || bill.names || "Walk-in Customer"}</span>
                        </div>

                        {bill.tableNumber && (
                          <div className="flex items-center">
                            <Hash size={16} className="mr-1" />
                            <span>Table {bill.tableNumber}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-3">
                        <div className="text-sm text-gray-500">
                          Payment:{" "}
                          <span
                            className={`font-medium ${
                              bill.paymentMethod === "CASH"
                                ? "text-green-600"
                                : bill.paymentMethod === "CARD"
                                  ? "text-blue-600"
                                  : "text-purple-600"
                            }`}
                          >
                            {bill.paymentMethod || "Not Set"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          Items: <span className="font-medium text-gray-700">{bill.items?.length || 0}</span>
                        </div>
                        {hasValidMobileNumber(bill) && (
                          <div className="text-sm text-gray-500 mt-1">
                            Mobile: <span className="font-medium text-gray-700">{bill.mobileNumber}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 sm:mt-0 sm:text-right">
                      <div className="text-2xl font-bold text-gray-800">₹{bill.totalAmount.toFixed(2)}</div>

                      <div className="mt-4 flex flex-wrap justify-end gap-2">
                        <button
                          className="px-4 py-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 flex items-center transition-all"
                          onClick={() => handlePrint(bill._id)}
                        >
                          <Printer size={16} className="mr-1" />
                          Print
                        </button>

                        {/* WhatsApp Send Button - Only show if mobile number exists */}
                        {hasValidMobileNumber(bill) && (
                          <button
                            className="px-4 py-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 flex items-center transition-all"
                            onClick={() => handleWhatsAppSend(bill)}
                          >
                            <MessageCircle size={16} className="mr-1" />
                            WhatsApp
                          </button>
                        )}

                        {isBillEditable(bill) && (
                          <>
                            <button
                              className="px-4 py-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 flex items-center transition-all"
                              onClick={() => handleEditBill(bill)}
                            >
                              <Edit2 size={16} className="mr-1" />
                              Edit
                            </button>

                            <button
                              className="px-4 py-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 flex items-center transition-all"
                              onClick={() => handleBillDetails(bill)}
                            >
                              <Tag size={16} className="mr-1" />
                              Details
                            </button>

                            <button
                              className="px-4 py-2 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 flex items-center transition-all"
                              onClick={() => {
                                setSelectedBill(bill)
                                setActiveModal(MODAL_TYPES.PAYMENT)
                              }}
                            >
                              <CreditCard size={16} className="mr-1" />
                              Payment
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AccountSwitch Modal */}
      {showAccountSwitch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg relative">
            <button
              onClick={() => setShowAccountSwitch(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            >
              <X size={24} />
            </button>
            <AccountSwitch />
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {activeModal === MODAL_TYPES.WHATSAPP && selectedBill && (
        <WhatsAppSender bill={selectedBill} restaurantInfo={restaurantInfo} onClose={() => setActiveModal(null)} />
      )}

      {/* Render additional modals */}
      {renderEditBillModal()}
      {renderMenuModal()}
      {renderVariantModal()}
      {renderPaymentMethodModal()}
      {renderDetailsModal()}
    </div>
  )
}

export default Dashboard
